import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, CalendarClock, Rocket, Siren, Train } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { useFactory } from "../../store";
import { type IssueDetail, OPEN_RELEASE, type ReleaseTarget } from "../../types";
import { Picker, type PickerOption } from "../issues/picker";
import { Button } from "../ui/button";
import { ScheduleReleaseDialog, UrgentReleaseDialog } from "./release-dialogs";
import { formatSchedule, ReleaseKindIcon, releaseSummaryLabel } from "./release-meta";

const NEXT = "__standard__";
const URGENT = "__urgent__";
const SCHEDULE = "__schedule__";
const NONE = "__none__";

/** The Release row for people who cannot change it or open release pages. */
export function ReleaseSummary({ issue }: { issue: IssueDetail }) {
  const label = issue.release ? releaseSummaryLabel(issue.release) : null;
  if (!label) return <span className="text-xs text-muted-foreground">None</span>;
  return (
    <span className="flex items-center gap-2 text-sm">
      {label.icon}
      <span className="truncate">{label.text}</span>
    </span>
  );
}

/**
 * The Release row in the issue sidebar: shows where the issue ships and lets a
 * person move it to the next standard release, promote it to an urgent
 * release, schedule it, join an open release, or take it out.
 */
export function ReleaseProperty({ issue, onChanged }: { issue: IssueDetail; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const { state } = useFactory();
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const releases = useQuery({
    queryKey: ["releases", "open"],
    queryFn: () => api.releases({ status: "open,shipping", limit: 50 }),
    staleTime: 15_000,
  });
  const open = (releases.data?.releases ?? []).filter((release) => release.status === "open");
  const scheduled = open
    .filter((release) => release.kind === "scheduled")
    .sort((a, b) => (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? ""));
  const assign = useMutation({
    mutationFn: (target: ReleaseTarget) => api.setIssueRelease(issue.identifier, target),
    onSuccess: ({ release }) => {
      toast.success(
        release ? `${issue.identifier} ships in ${release.release.name}` : "Removed from its release",
      );
      setUrgentOpen(false);
      setScheduleOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["releases"] });
      onChanged();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not update the release"),
  });

  const current = issue.release;
  const currentOpen = current ? OPEN_RELEASE.has(current.status) : false;
  const mergeItem = issue.taskId ? state?.mergeQueue.find((item) => item.taskId === issue.taskId) : undefined;
  const inHotfixLane = state?.tasks.find((task) => task.id === issue.taskId)?.lane === "hotfix";

  const options: PickerOption[] = [
    { value: NEXT, label: "Next standard release", icon: <Train className="size-3.5" /> },
    { value: URGENT, label: "Urgent release…", icon: <Siren className="size-3.5" /> },
    { value: SCHEDULE, label: "Schedule…", icon: <CalendarClock className="size-3.5" /> },
    ...open
      .filter((release) => release.kind !== "standard" && release.id !== current?.id)
      .map((release) => ({
        value: release.id,
        label: release.name,
        icon: <ReleaseKindIcon kind={release.kind} />,
        hint: release.kind === "scheduled" ? formatSchedule(release.scheduledFor) : `${release.issueCount}`,
      })),
    ...(currentOpen ? [{ value: NONE, label: "No release" }] : []),
  ];

  const onPick = (value: string) => {
    if (value === URGENT) return setUrgentOpen(true);
    if (value === SCHEDULE) return setScheduleOpen(true);
    if (value === NEXT) return assign.mutate({ kind: "standard" });
    if (value === NONE) return assign.mutate({ kind: "none" });
    assign.mutate({ kind: "existing", releaseId: value });
  };

  const label = current ? releaseSummaryLabel(current) : null;
  const shipped = current && !currentOpen && current.status !== "cancelled";

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        {shipped ? (
          <Button variant="ghost" size="sm" className="-ml-2 h-7 justify-start gap-2 px-2" asChild>
            <Link to={`/releases/${current.id}`}>
              {label?.icon}
              {label?.text}
            </Link>
          </Button>
        ) : (
          <Picker
            multiple={false}
            options={options}
            selected={current && currentOpen ? [current.kind === "standard" ? NEXT : current.id] : []}
            onToggle={onPick}
            placeholder="Release…"
          >
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 h-7 justify-start gap-2 px-2"
              disabled={assign.isPending}
            >
              {label ? (
                <>
                  {label.icon}
                  <span className="truncate">{label.text}</span>
                </>
              ) : (
                <>
                  <Rocket className="size-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {issue.statusKey === "done" ? "Not in a release" : "Next release when done"}
                  </span>
                </>
              )}
            </Button>
          </Picker>
        )}
        {current && (currentOpen || current.status === "cancelled") && (
          <Button variant="ghost" size="icon-sm" className="size-7" asChild aria-label="Open release">
            <Link to={`/releases/${current.id}`}>
              <ArrowUpRight className="size-3.5" />
            </Link>
          </Button>
        )}
      </div>
      {mergeItem?.status === "held" && (
        <p className="text-xs text-muted-foreground">
          Pull request approved; merge held until the release ships.
        </p>
      )}
      {current?.status === "shipping" && (
        <p className="text-xs text-muted-foreground">
          Shipping: publishes once every issue in the release is done.
        </p>
      )}
      <UrgentReleaseDialog
        open={urgentOpen}
        onOpenChange={setUrgentOpen}
        issueIdentifier={issue.identifier}
        hasPullRequest={Boolean(issue.prUrl)}
        defaultFromLastRelease={Boolean(inHotfixLane)}
        pending={assign.isPending}
        onConfirm={({ fromLastRelease }) => assign.mutate({ kind: "urgent", fromLastRelease })}
      />
      <ScheduleReleaseDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        issueIdentifier={issue.identifier}
        scheduled={scheduled.filter((release) => release.id !== current?.id)}
        pending={assign.isPending}
        onConfirm={(choice) =>
          assign.mutate(
            "releaseId" in choice
              ? { kind: "existing", releaseId: choice.releaseId }
              : { kind: "scheduled", scheduledFor: choice.scheduledFor },
          )
        }
      />
    </div>
  );
}
